import dayjs, { Dayjs } from "dayjs";
import { LabellerDetails } from "src/store/api/generatedApi";

export const ACCURACY_METRICS = ["material", "area", "timing"] as const;
export type AccuracyMetric = (typeof ACCURACY_METRICS)[number];

export const isAccuracyMetric = (value: string): value is AccuracyMetric =>
  (ACCURACY_METRICS as readonly string[]).includes(value);

export const VIEW_LABELS_PRE_ROLL_MINUTES = 10;

const getViewStartTime = (start: Dayjs): Dayjs => {
  const dayStart = start.startOf("day");
  const preRollStart = start.subtract(VIEW_LABELS_PRE_ROLL_MINUTES, "minute");
  return preRollStart.isBefore(dayStart) ? dayStart : preRollStart;
};

export const buildViewLabelsUrl = ({
  tenantName,
  location,
  labeller,
  labellerEventStart,
}: {
  tenantName: string;
  location: string;
  labeller: LabellerDetails;
  labellerEventStart: string;
}): string => {
  const start = dayjs.utc(labellerEventStart);
  const viewTime = getViewStartTime(start);
  const params = new URLSearchParams({
    date: start.format("YYYY-MM-DD"),
    time: viewTime.toISOString(),
    labeller: JSON.stringify(labeller),
  });
  if (labeller.labeller_type === "model" && labeller.name) {
    params.set("model", labeller.name);
  }
  return `/${tenantName}/tool/view-labels/${encodeURIComponent(location)}?${params.toString()}`;
};

export const buildManualReviewUrl = ({
  tenantName,
  location,
  labellerEventStart,
}: {
  tenantName: string;
  location: string;
  labellerEventStart: string;
}): string => {
  const windowStart = getViewStartTime(dayjs.utc(labellerEventStart));
  const dateParam = windowStart.format("YYYY-MM-DDTHH:mm:ss[Z]");
  return `/${tenantName}/tool/manual-review/${encodeURIComponent(location)}?date=${dateParam}`;
};

export const buildAccuracyPageUrl = ({
  tenantName,
  labellerId,
  metric,
  predicted,
  actual,
  location,
  start,
  end,
}: {
  tenantName: string;
  labellerId: string;
  metric: AccuracyMetric;
  predicted?: string;
  actual?: string;
  location: string;
  start: Dayjs | null;
  end: Dayjs | null;
}): string => {
  const params = new URLSearchParams({ metric });
  if (location) params.set("location", location);
  if (predicted) params.set("predicted", predicted);
  if (actual) params.set("actual", actual);
  if (start) params.set("start", start.utc(true).toISOString());
  if (end) params.set("end", end.utc(true).toISOString());
  return `/${tenantName}/labellers/${encodeURIComponent(
    labellerId
  )}/accuracy?${params.toString()}`;
};
